import React, { useState, useEffect } from "react";
import CrudManager from "../../common/CrudManager";
import {
  UserCog,
  Edit2,
  Trash2,
  Mail,
  Shield,
  Loader2,
  Calendar,
} from "lucide-react";

import { toast } from "sonner";
import { GetAxios } from "../../../helpers/axiosHelpers";

const AdminUserManager = ({ onClose }) => {
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      setLoadingRoles(true);
      const res = await GetAxios("/roles");
      if (res.success) setRoles(res.data);
    } catch (err) {
      toast.error("Could not load roles");
    } finally {
      setLoadingRoles(false);
    }
  };

  const getFields = (isEditing) => [
    {
      name: "fullName",
      label: "Full Name",
      type: "text",
      placeholder: "e.g., John Doe",
      required: true,
      fullWidth: true,
    },
    {
      name: "email",
      label: "Email Address",
      type: "email",
      placeholder: "e.g., john@example.com",
      required: true,
    },
    {
      name: "roleId",
      label: "System Role",
      type: "select",
      required: true,
      options: roles.map((role) => ({
        value: role.id,
        label: `${role.name} (Lvl ${role.level})`,
      })),
      valueExtractor: (item) => item.roleId || item.role?.id,
    },
    {
      name: "password",
      label: "Access Password",
      type: "password",
      placeholder: "••••••••",
      required: !isEditing, // Only required on creation
      hideOnEdit: true,
    },
  ];

  const formatCreateData = (formData) => ({
    fullName: formData.fullName.trim(),
    email: formData.email.trim().toLowerCase(),
    password: formData.password,
    roleId: formData.roleId,
  });

  const formatUpdateData = (formData) => ({
    fullName: formData.fullName.trim(),
    email: formData.email.trim().toLowerCase(),
    roleId: formData.roleId,
  });

  const renderItem = (user, onEdit, onDelete) => (
    <div className="flex items-center justify-between w-full group">
      <div className="flex items-center space-x-4">
        {/* Avatar/Icon with Status Ring */}
        <div className="relative">
          <div className="w-14 h-14 bg-slate-50 border border-slate-100 rounded-2xl flex items-center justify-center text-subidha-blue group-hover:bg-subidha-blue group-hover:text-white transition-all duration-300">
            <UserCog size={24} />
          </div>
          <div
            className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white ${user.isActive ? "bg-green-500" : "bg-red-500"}`}
          />
        </div>

        <div className="space-y-1">
          <div className="flex items-center space-x-2">
            <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight">
              {user.fullName}
            </h3>
            <span
              className={`text-[9px] font-black px-2 py-0.5 rounded-md uppercase tracking-tighter ${
                user.isActive
                  ? "bg-green-100 text-green-600"
                  : "bg-red-100 text-red-600"
              }`}
            >
              {user.isActive ? "Active" : "Deactivated"}
            </span>
          </div>

          <div className="flex flex-col space-y-0.5">
            <div className="flex items-center text-[11px] text-slate-500 font-bold">
              <Mail className="w-3 h-3 mr-1.5 text-slate-400" />
              {user.email}
            </div>
            <div className="flex items-center text-[11px] text-subidha-blue font-bold">
              <Shield className="w-3 h-3 mr-1.5 opacity-70" />
              {user.role?.name || "No Role"}{" "}
              <span className="mx-1 text-slate-300">•</span> Level{" "}
              {user.role?.level || 0}
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center space-x-1">
        {onEdit && (
          <button
            onClick={() => onEdit(user)}
            className="p-2.5 text-slate-400 hover:text-subidha-blue hover:bg-blue-50 rounded-xl transition-all"
          >
            <Edit2 className="w-4 h-4" />
          </button>
        )}
        {onDelete && (
          <button
            onClick={() => onDelete(user)}
            className="p-2.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );

  if (loadingRoles) {
    return (
      <div className="bg-white rounded-[2.5rem] shadow-xl border border-slate-100 flex items-center justify-center py-24">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-subidha-blue animate-spin mx-auto mb-4" />
          <p className="text-xs font-black text-slate-400 uppercase tracking-[0.2em]">
            Synchronizing Roles...
          </p>
        </div>
      </div>
    );
  }

  return (
    <CrudManager
      title="Admin User Directory"
      apiEndpoint="/adminuser"
      fields={getFields}
      renderItem={renderItem}
      onClose={onClose}
      icon={UserCog}
      emptyMessage="No admin staff found. Add your first teammate!"
      formatCreateData={formatCreateData}
      formatUpdateData={formatUpdateData}
    />
  );
};

export default AdminUserManager;
